<template>
  <div
    :class="['bg-gray-700 border-2 rounded-lg shadow-lg w-64 relative', selected ? 'border-green-400' : data.__validationError ? 'border-red-500' : 'border-gray-600']"
  >
    <!-- Header (altezza fissa: da questa dipende la posizione delle maniglie) -->
    <div
      class="bg-gray-800 px-3 rounded-t-md flex items-center gap-2 justify-between"
      :style="{ height: `${HEADER_HEIGHT}px` }"
    >
      <div class="flex items-center gap-2">
        <span class="material-icons text-green-400">call_split</span>
        <strong class="text-white">{{ $t('flowEditor.nodes.ifConditionNode.title') }}</strong>
      </div>
      <ValidationMarker :error="data.__validationError" />
    </div>

    <!-- Righe dei rami: altezza fissa, una per ramo piu' Altrimenti -->
    <div
      v-for="(branch, idx) in branches"
      :key="branch.id"
      class="px-3 flex flex-col justify-center overflow-hidden border-b border-gray-600 transition-colors"
      :class="{ 'bg-green-400/20': hovered === branch.id }"
      :style="{ height: `${ROW_HEIGHT}px` }"
      @mouseenter="hovered = branch.id"
      @mouseleave="hovered = null"
    >
      <span class="font-bold text-sm text-white truncate">{{ branchName(branch, idx, t) }}</span>
      <span class="text-xs text-gray-300 truncate" v-tooltip="branchSummary(branch, t)">{{ branchSummary(branch, t) }}</span>
    </div>
    <div
      class="px-3 flex items-center overflow-hidden rounded-b-md transition-colors"
      :class="{ 'bg-gray-500/40': hovered === 'else' }"
      :style="{ height: `${ROW_HEIGHT}px` }"
      @mouseenter="hovered = 'else'"
      @mouseleave="hovered = null"
    >
      <span class="text-sm italic text-gray-300 truncate">{{ $t('flowEditor.nodes.ifConditionNode.elseLabel') }}</span>
    </div>

    <!-- Punti di connessione -->
    <Handle type="target" :position="Position.Left" :style="{ top: `${HEADER_HEIGHT / 2}px` }" />
    <Handle
      v-for="(branch, idx) in branches"
      :id="`handle_${branch.id}`"
      :key="branch.id"
      type="source"
      :position="Position.Right"
      :style="{ top: `${handleTop(idx)}px` }"
      :class="{ 'ring-2 ring-green-300': hovered === branch.id }"
    />
    <Handle
      id="handle_else"
      type="source"
      :position="Position.Right"
      :style="{ top: `${handleTop(branches.length)}px` }"
      :class="{ 'ring-2 ring-gray-100': hovered === 'else' }"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Handle, Position, useVueFlow } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { branchName, branchSummary } from '@/utils/ifConditionText';
import ValidationMarker from './ValidationMarker.vue';

/**
 * IfConditionNode.vue
 *
 * Nodo canvas della condizione multi-ramo: una riga e una maniglia di uscita per
 * ramo (id `handle_<branch.id>`, mai la posizione) piu' la riga "Altrimenti"
 * (`handle_else`). Header e righe hanno altezza fissa in px: le maniglie sono
 * posizionate da quelle costanti, quindi non servono misure a runtime.
 *
 * @example
 * <IfConditionNode :data="node.data" :selected="node.selected" />
 */
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  nodeId: {
    type: String,
    default: null,
  },
});

const { t } = useI18n();

const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 44;

const hovered = ref(null);
const branches = computed(() => (Array.isArray(props.data.branches) ? props.data.branches : [])
  .filter(Boolean)
  .map(b => (Array.isArray(b.rules) ? b : { ...b, rules: [] })));

// Vue Flow ricalcola la posizione delle maniglie solo quando cambiano le
// dimensioni del nodo: spostare un ramo non le cambia, quindi i collegamenti
// resterebbero disegnati dalle posizioni vecchie. Lo chiediamo noi.
const { updateNodeInternals } = useVueFlow();
watch(
  () => branches.value.map(b => b.id).join('|'),
  () => { if (props.nodeId) updateNodeInternals([props.nodeId]); },
  { flush: 'post' },
);

// Centro della riga `index` (compreso il bordo del nodo, gia' fuori dal conto
// perche' le maniglie sono posizionate rispetto al padding box).
const handleTop = (index) => HEADER_HEIGHT + index * ROW_HEIGHT + ROW_HEIGHT / 2;
</script>
